import { useMapCatalogue } from './MapCatalogueProvider';
import { useCallback, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from '../i18n';
import {
  DEFAULT_MAP_APPEARANCE,
  MAP_APPEARANCE_STORAGE_KEY,
  clampAppearance,
  type MapAppearance,
} from '../lib/mapAppearance';
import { useLocalStorage } from '../lib/useLocalStorage';
import { Icon } from './Icon';
import { SurfaceIconButton } from './SurfaceIconButton';
import { HiddenPinTypesChip, HIDDEN_PIN_TYPES_CHIP_STACK } from './HiddenPinTypesChip';
import { MapAppearancePanel } from './MapAppearancePanel';
import { usePinTypeCatalog } from './PinTypeCatalogProvider';
import { MapLayersPicker, type BaseMapInfo, type OverlayInfo } from './MapLayersPicker';
import type { TileSource } from '../lib/mapTypes';

// ─── Shared layer state ───────────────────────────────────────────────────
//
// The authenticated catalogue comes from the host. Only layer selections are
// shared across the Map tab and its full-screen surfaces during this visit.

interface LayerState {
  /** null → whichever base map the API lists first. */
  baseMapId: string | null;
  enabledOverlayIds: ReadonlySet<string>;
}

const EMPTY_BASE_MAPS: BaseMapInfo[] = [];
const EMPTY_OVERLAY_MAPS: OverlayInfo[] = [];
const EMPTY_IDS: ReadonlySet<string> = new Set();
const INITIAL_LAYERS: LayerState = {
  baseMapId: null, enabledOverlayIds: EMPTY_IDS,
};

let layerState: LayerState = INITIAL_LAYERS;
const layerListeners = new Set<() => void>();

function setLayerState(next: Partial<LayerState>) {
  layerState = { ...layerState, ...next };
  for (const listener of layerListeners) listener();
}

function subscribeLayers(listener: () => void): () => void {
  layerListeners.add(listener);
  return () => { layerListeners.delete(listener); };
}

const getLayerState = () => layerState;

/** Test seam: forget the catalogue and the pick so each test starts clean. */
export function resetTeamMapLayersForTest() {
  layerState = INITIAL_LAYERS;
  for (const listener of layerListeners) listener();
}

/** Layer + appearance state, plus the UI to drive it, for one read-only map surface. */
export interface TeamMapPresentation {
  /** The layers and view-settings buttons — pass as `TeamMapView.extraControls`. */
  controls: ReactNode;
  /**
   * The panels those buttons open, plus any notice about the current view
   * settings — render inside the map's positioned container.
   */
  panels: ReactNode;
  appearance: MapAppearance;
  /** The base map to draw, or null before the catalogue has arrived. */
  tileSource: TileSource | null;
  /**
   * The catalogue id of that base map — what the print payload sends as its
   * flavour, since the catalogue's ids ARE the print server's flavours. Exposed
   * beside `tileSource` rather than derived from it: `tileSource` carries the
   * tile URL and nothing that names which layer it is.
   */
  baseMapId: string | null;
  overlayLayers: TileSource[];
  /** The catalogue ids of the overlays switched on — what the print payload
   *  reads to decide whether the sheet carries property borders. */
  enabledOverlayIds: ReadonlySet<string>;
  /** True while a layers or view-settings panel is open — see `closePanels`. */
  activeOverlays: readonly { id: string; label: string }[];
  cataloguePending: boolean;
  catalogueError: boolean;
  retryCatalogue?: () => void;
  panelOpen: boolean;
  /**
   * Dismiss whichever panel is open. A surface whose Escape key closes the whole
   * surface should call this first, so Escape dismisses the panel rather than
   * discarding the surface.
   */
  closePanels: () => void;
}

/**
 * Map layers and view settings for a read-only team map.
 *
 * One hook rather than three copies: the Map tab, its full-screen preview and the
 * print surface all offer these controls, and a member who sets the map up on one
 * should find it that way on the others. Appearance lives in the same persisted
 * store the map editor's own panel writes, so that holds across the editor too.
 *
 * The layer selection is shared between surfaces but NOT persisted across visits —
 * see the store above.
 */
export function useTeamMapPresentation({
  side = 'right',
}: {
  /**
   * Which edge the panels anchor to. The print surface passes 'left' because its
   * print panel permanently holds the right slot these would otherwise take.
   */
  side?: 'left' | 'right';
} = {}): TeamMapPresentation {
  // The layers picker and appearance panel come from the editor, and their strings
  // live in the 'map' namespace alongside it.
  const { t } = useTranslation('map');

  const layers = useSyncExternalStore(subscribeLayers, getLayerState, getLayerState);
  const catalogue = useMapCatalogue();
  const baseMaps = catalogue.data?.baseMaps ?? EMPTY_BASE_MAPS;
  const overlayMaps = catalogue.data?.overlayMaps ?? EMPTY_OVERLAY_MAPS;
  const legacyTile = useMemo<TileSource | null>(() => catalogue.data?.url ? { url: catalogue.data.url, generation: catalogue.data.generation ?? 1, widths: catalogue.data.widths ?? [] } : null, [catalogue.data]);

  const [layersOpen, setLayersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Stable: consumers put it in effect dependency arrays (the Escape handlers).
  const closePanels = useCallback(() => { setLayersOpen(false); setSettingsOpen(false); }, []);

  const [appearanceRaw, setAppearance] = useLocalStorage<MapAppearance>(
    MAP_APPEARANCE_STORAGE_KEY,
    DEFAULT_MAP_APPEARANCE,
  );
  const catalog = usePinTypeCatalog();
  const appearance = clampAppearance(appearanceRaw, catalog.knownPredicate);

  const selectedBaseMap =
    baseMaps.find((m) => m.id === layers.baseMapId) ?? baseMaps[0];

  // Memoised: both reach effect dependencies in the tile layers, so a fresh object
  // per render would restart the tile fetch every render.
  const tileSource = useMemo<TileSource | null>(
    () =>
      selectedBaseMap
        ? { url: selectedBaseMap.url, generation: selectedBaseMap.generation, widths: selectedBaseMap.widths ?? [] }
        : legacyTile,
    [selectedBaseMap, legacyTile],
  );

  const overlayLayers = useMemo<TileSource[]>(
    () =>
      overlayMaps
        .filter((o) => layers.enabledOverlayIds.has(o.id) && o.url)
        .map((o) => ({ url: o.url, generation: o.generation, widths: o.widths ?? [] })),
    [overlayMaps, layers.enabledOverlayIds],
  );

  const activeOverlays = useMemo(() => overlayMaps.filter((o) => layers.enabledOverlayIds.has(o.id)), [overlayMaps, layers.enabledOverlayIds]);

  const controls = (
    <>
      <SurfaceIconButton
        title={t('controls.mapLayers')}
        onClick={() => { setLayersOpen((v) => !v); setSettingsOpen(false); }}
        active={layersOpen}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      </SurfaceIconButton>
      <SurfaceIconButton
        title={t('controls.appearance')}
        onClick={() => { setSettingsOpen((v) => !v); setLayersOpen(false); }}
        active={settingsOpen}
      >
        <Icon name="settings" size={20} />
      </SurfaceIconButton>
    </>
  );

  /**
   * The chip owns the top-right corner while it is up, so anything else in that
   * column starts below it. Only surfaces whose panels anchor right share the
   * column — the print surface anchors them left, where the chip never goes.
   */
  const chipStack =
    appearance.hiddenPinTypes.length > 0 ? HIDDEN_PIN_TYPES_CHIP_STACK : 0;
  const panelTopOffset = side === 'right' ? chipStack : 0;

  const panels = (
    <>
      {layersOpen && (
        <MapLayersPicker
          baseMaps={baseMaps}
          overlayMaps={overlayMaps}
          selectedMapId={selectedBaseMap?.id ?? null}
          enabledOverlays={layers.enabledOverlayIds}
          onSelectMap={(m) => setLayerState({ baseMapId: m.id })}
          onToggleOverlay={(id) => {
            const next = new Set(layers.enabledOverlayIds);
            if (next.has(id)) next.delete(id); else next.add(id);
            setLayerState({ enabledOverlayIds: next });
          }}
          onClose={() => setLayersOpen(false)}
          side={side}
          topOffset={panelTopOffset}
        />
      )}
      {settingsOpen && (
        <MapAppearancePanel
          value={appearance}
          onChange={setAppearance}
          onClose={() => setSettingsOpen(false)}
          side={side}
          topOffset={panelTopOffset}
        />
      )}
      {/*
        CLUB-667: rides along with the panels rather than being returned
        separately, so every surface that renders them — Map tab, full-screen,
        print — states the filter without a call-site change. The print surface
        is the one that must not miss it, and it is the one most easily
        forgotten.
      */}
      <HiddenPinTypesChip
        hiddenPinTypes={appearance.hiddenPinTypes}
        withBackground={appearance.pinBackground}
        onShowAll={() => setAppearance({ ...appearance, hiddenPinTypes: [] })}
        onShowType={(type) =>
          setAppearance({
            ...appearance,
            hiddenPinTypes: appearance.hiddenPinTypes.filter((t) => t !== type),
          })
        }
        onOpenList={() => {
          setLayersOpen(false);
          setSettingsOpen(false);
        }}
      />
    </>
  );

  return {
    cataloguePending: catalogue.pending,
    catalogueError: catalogue.error,
    retryCatalogue: catalogue.retry,
    controls,
    panels,
    appearance,
    tileSource,
    baseMapId: selectedBaseMap?.id ?? null,
    overlayLayers,
    enabledOverlayIds: layers.enabledOverlayIds,
    activeOverlays,
    panelOpen: layersOpen || settingsOpen,
    closePanels,
  };
}
